import { useEffect, useState } from "react";
import { History, Images, X } from "lucide-react";
import { api } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";

const PREVIEW_IMAGE_COUNT = 4;

/**
 * v3.9.37 (CEO #2) — "Previous work accomplished" section on a listing detail page.
 * Shows the provider's previous-work write-up plus a separate photo gallery stored
 * under the `<type>-prev` namespace. Renders nothing when there's neither text nor
 * photos, so it never leaves an empty card.
 */
export function PreviousWork({ text, type, id, showcase = false }: { text?: string | null; type: string; id: string; showcase?: boolean }) {
  const [imgs, setImgs] = useState<{ id: string; url: string }[]>([]);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.postImages(`${type}-prev`, id).then((r) => { if (alive) setImgs(r); }).catch(() => { if (alive) setImgs([]); });
    return () => { alive = false; };
  }, [type, id]);

  const body = (text || "").trim();
  useEscapeKey(() => setGalleryOpen(false), galleryOpen);
  if (!body && imgs.length === 0) return null;

  if (showcase) {
    const preview = imgs.slice(0, PREVIEW_IMAGE_COUNT);
    return (
      <section className="card p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-bold text-navy text-lg">Previous Work</h2>
            <p className="text-sm text-mut mt-1">Explore completed projects and previous work from this provider.</p>
          </div>
          {imgs.length > PREVIEW_IMAGE_COUNT && (
            <button type="button" onClick={() => setGalleryOpen(true)} className="text-sm font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 shrink-0">
              <Images className="w-4 h-4" />View all work <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
        {body && <p className="text-sm text-mut whitespace-pre-line mb-3">{body}</p>}
        {preview.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {preview.map((image, index) => (
              <button type="button" key={image.id} onClick={() => setGalleryOpen(true)} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-line bg-navy-50">
                <img src={api.postImageSrc(`${type}-prev`, id, image.id)} alt={`Previous work ${index + 1}`} loading="lazy" className="w-full h-full object-cover" />
                {index === PREVIEW_IMAGE_COUNT - 1 && imgs.length > PREVIEW_IMAGE_COUNT && <span className="absolute inset-0 grid place-items-center bg-black/55 text-white text-xl font-bold">+{imgs.length - PREVIEW_IMAGE_COUNT}</span>}
              </button>
            ))}
          </div>
        )}
        {galleryOpen && (
          <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-3 sm:p-6" onClick={() => setGalleryOpen(false)}>
            <section role="dialog" aria-modal="true" aria-labelledby="previous-work-dialog-title" className="w-full max-w-5xl max-h-[90vh] overflow-auto rounded-xl bg-white p-4 sm:p-6" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div><h2 id="previous-work-dialog-title" className="text-xl font-bold text-navy">Previous work</h2><p className="text-sm text-mut mt-1">{imgs.length} photo{imgs.length === 1 ? "" : "s"} from this provider</p></div>
                <button type="button" onClick={() => setGalleryOpen(false)} aria-label="Close previous work" className="btn-outline p-2"><X className="w-4 h-4" /></button>
              </div>
              {body && <p className="text-sm text-mut whitespace-pre-line mb-4">{body}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {imgs.map((image, index) => (
                  <img key={image.id} src={api.postImageSrc(`${type}-prev`, id, image.id)} alt={`Previous work ${index + 1}`} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-lg border border-line" />
                ))}
              </div>
            </section>
          </div>
        )}
      </section>
    );
  }

  return (
    <div className="card p-5">
      <h3 className="font-bold text-navy mb-2 flex items-center gap-2"><History className="w-4 h-4" />Previous work</h3>
      {body && <p className="text-sm text-mut whitespace-pre-line">{body}</p>}
      {imgs.length > 0 && (
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${body ? "mt-3" : ""}`}>
          {imgs.map((m) => (
            <button key={m.id} onClick={() => setLightbox(api.postImageSrc(`${type}-prev`, id, m.id))} className="group text-left">
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-navy-50 border border-line">
                <img src={api.postImageSrc(`${type}-prev`, id, m.id)} alt="Previous work" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setLightbox(null)}>
          <button aria-label="Close" className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-7 h-7" /></button>
          <img src={lightbox} alt="Previous work" className="max-w-3xl w-full max-h-[80vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
